import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import CardHeader from "@material-ui/core/CardHeader";
import Divider from "@material-ui/core/Divider";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

const hebrewLabels = {
  title: "לא מצאנו את מה שחיפשת",
  subtitle: "אנחנו מצטערים",
  description: "לא מצאנו צמיגים במידה שחיפשת. נשמח לדעת איזה דגם אתה מחפש ונשקול להביא אותו במשלוח הבא שלנו.",
  searchedSize: "המידה שחיפשת:",
  modelLabel: "איזה דגם/מותג אתה מחפש?",
  modelPlaceholder: "לדוגמה: Pirelli Angel GT, Michelin Road 5...",
  phoneLabel: "מספר טלפון ליצירת קשר",
  phonePlaceholder: "05X-XXXXXXX",
  emailLabel: "כתובת אימייל (אופציונלי)",
  emailPlaceholder: "example@domain.com",
  submitButton: "שלח בקשה",
  successMessage: "תודה! קיבלנו את הבקשה שלך ונחזור אליך בהקדם",
  requiredField: "שדה חובה",
};

const styles = (theme) => ({
  card: {
    maxWidth: 600,
    marginTop: "18px",
    marginLeft: "auto",
    marginRight: "auto",
    position: "relative",
    border: "2px solid #f50057",
  },
  cardHeader: {
    backgroundColor: "#f50057",
    color: "#ffffff",
    textAlign: "center",
    "& .MuiCardHeader-title": {
      fontSize: "1.2rem",
      fontWeight: "bold",
    },
    "& .MuiCardHeader-subheader": {
      color: "#ffffff",
    },
  },
  content: {
    padding: theme.spacing(3),
    textAlign: "center",
  },
  icon: {
    fontSize: 80,
    color: "#f50057",
    marginBottom: theme.spacing(2),
  },
  description: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    color: "#555",
    lineHeight: 1.6,
  },
  searchedSizeLabel: {
    fontWeight: "bold",
    fontSize: "1.1rem",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    color: "#333",
  },
  searchedSizeValue: {
    fontSize: "1.3rem",
    fontWeight: "bold",
    color: "#f50057",
    marginBottom: theme.spacing(3),
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  textField: {
    width: "100%",
  },
  actions: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  submitButton: {
    width: "80%",
    padding: theme.spacing(1.5),
    fontSize: "1rem",
    fontWeight: "bold",
  },
  successContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(3),
  },
  successIcon: {
    fontSize: 80,
    color: "#4caf50",
    marginBottom: theme.spacing(2),
  },
  successMessage: {
    fontSize: "1.1rem",
    color: "#4caf50",
    textAlign: "center",
    fontWeight: "bold",
  },
});

class TireRequestCard extends React.Component {
  state = {
    model: "",
    phone: "",
    email: "",
    isSubmitting: false,
    isSubmitted: false,
    errors: {},
  };

  validatePhone = (phone) => {
    // Israeli phone number validation (05X-XXXXXXX or 05XXXXXXXX)
    const phoneRegex = /^05\d{1}[-\s]?\d{7}$/;
    return phoneRegex.test(phone.replace(/\s+/g, ""));
  };

  validateEmail = (email) => {
    if (!email) return true; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  validateForm = () => {
    const { model, phone, email } = this.state;
    const errors = {};

    if (!model.trim()) {
      errors.model = hebrewLabels.requiredField;
    }

    if (!phone.trim()) {
      errors.phone = hebrewLabels.requiredField;
    } else if (!this.validatePhone(phone)) {
      errors.phone = "מספר טלפון לא תקין";
    }

    if (email && !this.validateEmail(email)) {
      errors.email = "כתובת אימייל לא תקינה";
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmit = () => {
    if (!this.validateForm()) {
      return;
    }

    const { model, phone, email } = this.state;
    const { tireSize, onSubmit } = this.props;

    this.setState({ isSubmitting: true });

    const requestData = {
      tireSize,
      model: model.trim(),
      phone: phone.trim(),
      email: email.trim(),
    };

    onSubmit(requestData)
      .then(() => {
        this.setState({
          isSubmitting: false,
          isSubmitted: true,
        });
      })
      .catch(() => {
        this.setState({ isSubmitting: false });
        alert("אירעה שגיאה בשליחת הבקשה. אנא נסה שוב מאוחר יותר.");
      });
  };

  handleInputChange = (field) => (event) => {
    this.setState({
      [field]: event.target.value,
      errors: {
        ...this.state.errors,
        [field]: undefined,
      },
    });
  };

  render() {
    const { classes, tireSize } = this.props;
    const { model, phone, email, isSubmitting, isSubmitted, errors } =
      this.state;

    if (isSubmitted) {
      return (
        <Card className={classes.card}>
          <div className={classes.successContainer}>
            <CheckCircleIcon className={classes.successIcon} />
            <Typography className={classes.successMessage}>
              {hebrewLabels.successMessage}
            </Typography>
          </div>
        </Card>
      );
    }

    return (
      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          title={hebrewLabels.title}
          subheader={hebrewLabels.subtitle}
        />
        <Divider />
        <CardContent className={classes.content}>
          <ErrorOutlineIcon className={classes.icon} />

          <Typography className={classes.description}>
            {hebrewLabels.description}
          </Typography>

          <Typography className={classes.searchedSizeLabel}>
            {hebrewLabels.searchedSize}
          </Typography>
          <Typography className={classes.searchedSizeValue}>
            {tireSize}
          </Typography>

          <div className={classes.form}>
            <TextField
              label={hebrewLabels.modelLabel}
              placeholder={hebrewLabels.modelPlaceholder}
              value={model}
              onChange={this.handleInputChange("model")}
              error={!!errors.model}
              helperText={errors.model}
              className={classes.textField}
              variant="outlined"
              multiline
              rows={2}
              disabled={isSubmitting}
              required
            />

            <TextField
              label={hebrewLabels.phoneLabel}
              placeholder={hebrewLabels.phonePlaceholder}
              value={phone}
              onChange={this.handleInputChange("phone")}
              error={!!errors.phone}
              helperText={errors.phone}
              className={classes.textField}
              variant="outlined"
              disabled={isSubmitting}
              required
            />

            <TextField
              label={hebrewLabels.emailLabel}
              placeholder={hebrewLabels.emailPlaceholder}
              value={email}
              onChange={this.handleInputChange("email")}
              error={!!errors.email}
              helperText={errors.email}
              className={classes.textField}
              variant="outlined"
              disabled={isSubmitting}
            />
          </div>
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button
            variant="contained"
            color="secondary"
            className={classes.submitButton}
            onClick={this.handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              hebrewLabels.submitButton
            )}
          </Button>
        </CardActions>
      </Card>
    );
  }
}

TireRequestCard.propTypes = {
  classes: PropTypes.object.isRequired,
  tireSize: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default withStyles(styles)(TireRequestCard);

